import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { FormikErrors, FormikHelpers } from 'formik';
import React, { createContext, useContext, useState } from 'react';
import * as Yup from 'yup';
import { AnyObject } from 'yup';
import { apiErrorResponse } from '../../../../../services/entities/impl/blfApi/schemas/apiResponse';
import SubmitSnackbar from '../../components/SubmitSnackBar';

export enum ActionTypes {
  create = 'create',
  update = 'update',
  delete = 'delete',
}

export enum SubmitStatuses {
  idle = 'idle',
  pending = 'pending',
  success = 'success',
  error = 'error',
}

export interface GetEntityResponse<T> {
  currentData: T | null;
  isLoading: boolean;
  isFetching: boolean;
}

// Extra validation beyond Yup schema, such as checking the database for unique names
export interface ExtraValidationFunction<T> {
  (values: T): FormikErrors<T>;
}

/**
 * Validates form data against a Yup schema and performs custom validation. First, it validates the data using the provided
 * Yup schema. If the data fails validation, it aggregates the errors. Then, it optionally runs an extra validation function
 * if provided. This function is useful for integrating custom validation logic that may not be covered by Yup, ensuring both
 * schema-based and custom validations are applied.
 *
 * @param value The current values of the form to validate (from Formik).
 * @param schema The Yup validation schema.
 * @param extraValidation An optional custom validation function that returns error messages for specific fields.
 * @returns An object containing any validation errors, keyed by field name to match Formik's expected error type.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function formValidator<T extends Record<string, any>>({
  value,
  schema,
  extraValidation,
}: {
  value: T;
  schema: Yup.ObjectSchema<T>;
  extraValidation?: ExtraValidationFunction<T>;
}): Promise<FormikErrors<T>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: Record<string, any> = {};

  try {
    await schema.validate(value, { abortEarly: false });
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      err.inner.forEach((error) => {
        if (error.path) {
          errors[error.path] = error.message;
        }
      });
    }
  }

  let formikErrors: FormikErrors<T> = errors;

  if (extraValidation) {
    const extraValidationErrors = extraValidation(value);
    formikErrors = { ...formikErrors, ...extraValidationErrors };
  }

  return formikErrors;
}

export interface CRUDState<T extends AnyObject, K extends AnyObject>
  extends Omit<CRUDProviderProps<T, K>, 'children'> {
  isValidEntity: (entity: Partial<T>) => boolean;
  isValidFieldChange: (key: string, fieldName: string) => boolean;
  formSubmitHandler: ({
    helpers,
    operationCallback,
  }: {
    helpers: FormikHelpers<T>;
    operationCallback: () => Promise<void | AnyObject> | void;
  }) => Promise<void | AnyObject>;
  currEntity: T | null;
}

// Create the context
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CRUDContext = createContext<CRUDState<any, any> | undefined>(
  undefined
);

export interface CRUDProviderProps<T extends AnyObject, K extends AnyObject> {
  children: React.ReactNode;
  yupSchemaIn: Yup.ObjectSchema<T>;
  yupSchemaOut: Yup.ObjectSchema<K>;
  handleAdd: (obj: Partial<T>) => Promise<AnyObject | void>;
  handleUpdate: (obj: Partial<T>) => Promise<AnyObject | void>;
  handleDelete: (id: string) => Promise<AnyObject | void>;
  getCurrEntityHook: () => GetEntityResponse<T | K>;
  extraValidation?: ExtraValidationFunction<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitSuccess?: (res: any) => Promise<void>;
}

// Create the provider
export function CRUDProvider<T extends AnyObject, K extends AnyObject>({
  children,
  ...props
}: CRUDProviderProps<T, K>) {
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const currEntity = props.getCurrEntityHook().currentData;

  const isValidEntity = React.useCallback(
    (entity: Partial<T>) => {
      try {
        props.yupSchemaIn.validateSync(entity, { abortEarly: true });
        return true;
      } catch {
        return false;
      }
    },
    [props.yupSchemaIn]
  );

  const isValidFieldChange = React.useCallback(
    (key: string, value: string | number) => {
      try {
        const validationResult = props.yupSchemaIn.validateSyncAt(key, {
          [key]: value,
        });

        return !!validationResult;
      } catch {
        return false;
      }
    },
    [props.yupSchemaIn]
  );

  async function formSubmitHandler<T extends AnyObject>({
    helpers,
    operationCallback,
  }: {
    helpers: FormikHelpers<T>;
    operationCallback: () => Promise<void | AnyObject> | void;
  }) {
    try {
      setErrorMessage('');
      helpers.setStatus({ status: SubmitStatuses.pending });

      const res = await operationCallback();
      console.log('Form submit handler response: ', res);

      if (res?.error) {
        console.debug('API error: ', res.error);
        let errorMessage = 'Unknown error';

        if ((res.error as FetchBaseQueryError).data !== undefined) {
          errorMessage = (
            (res.error as FetchBaseQueryError).data as apiErrorResponse
          ).detail as string;
          if (typeof errorMessage !== 'string') {
            errorMessage = 'unknown error';
          }
        }
        throw new Error(errorMessage as string);
      }

      helpers.setStatus({ status: SubmitStatuses.success });

      if (props.onSubmitSuccess) {
        await props.onSubmitSuccess(res);
      }

      // clear status after 3 seconds
      setTimeout(() => {
        helpers.setStatus({ status: SubmitStatuses.idle });
        setSnackbarOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Caught error: ', error);

      setErrorMessage((error as Error).message || 'Unknown error');

      helpers.setStatus({
        status: SubmitStatuses.error,
        message: (error as Error).message || 'Unknown error',
      });

      // in error case give user 10 seconds to understand what happened
      setTimeout(() => {
        helpers.setStatus({ status: SubmitStatuses.idle });
        setSnackbarOpen(false);
      }, 10000);
    } finally {
      setSnackbarOpen(true);
    }
  }

  const state = {
    isValidEntity,
    isValidFieldChange,
    formSubmitHandler,
    currEntity,
    ...props,
  };

  return (
    <CRUDContext.Provider value={state}>
      <SubmitSnackbar snackbarOpen={snackbarOpen} errorMessage={errorMessage} />
      {children}
    </CRUDContext.Provider>
  );
}

export function useCRUDContext() {
  const context = useContext(CRUDContext);
  if (!context) {
    throw new Error('useOnboardContext must be used within a OnboardContext');
  }
  return context;
}
